/**
 * OwnerCheck — route guard that restricts rendering to the app Owner.
 *
 * Mirrors SuperAdminCheck's shape but uses the `isOwner` predicate
 * from roleUtils. Backend enforces the same restriction server-side
 * (requireOwner middleware); this component is UX-only, keeping
 * non-owner super admins out of surfaces whose contents would have
 * been 403s anyway.
 *
 * @module Pages/Admin/OwnerCheck
 */

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import AuthLoader from '../../Components/Helper/AuthLoader';
import { setUserFromLocalStorage, updateUser } from '../../Store/userSlice';
import { isSuperAdmin, isOwner } from '../../utils/roleUtils';
import { getAdminAccessMe } from '../../api/api';

const OwnerCheck = ({ element }) => {
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!user || !user.email) {
      dispatch(setUserFromLocalStorage());
    }
    setIsLoaded(true);
  }, [dispatch, user]);

  // If super admin but isOwner flag hasn't been populated yet, fetch it
  // so the guard below doesn't misfire on a freshly-loaded user record.
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSuperAdmin(user)) return;
    if (user?.isOwner !== undefined) return;
    getAdminAccessMe()
      .then((res) => {
        dispatch(updateUser({ isOwner: !!res?.data?.data?.isOwner }));
      })
      .catch(() => {
        dispatch(updateUser({ isOwner: false }));
      });
  }, [isLoaded, user, dispatch]);

  if (!isLoaded) {
    return <AuthLoader />;
  }

  if (!user || !user.email) {
    return <Navigate to="/teacher-login" replace />;
  }

  // If isOwner is still undefined (fetch in-flight), render a spinner.
  // The guard fires only once the backend has confirmed.
  if (!isSuperAdmin(user)) {
    return <Navigate to="/myprofile" replace />;
  }
  if (user.isOwner === undefined) {
    return <AuthLoader />;
  }
  if (!isOwner(user)) {
    return <Navigate to="/myprofile" replace />;
  }

  return element;
};

OwnerCheck.propTypes = {
  element: PropTypes.node.isRequired,
};

export default OwnerCheck;
